import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import BusinessCard from './BusinessCard';
import { Loader2 } from 'lucide-react';

interface Business {
  id: number;
  store_name: string;
  owner_name: string;
  location: string;
  description?: string;
  avatar_url?: string;
  verified: boolean;
  business_type_id: string;
  profession_id?: number;
  created_at: string;
  slug?: string;
}

interface BusinessWithType extends Business {
  business_type: string;
  profession_name?: string;
}

const RecentlyAddedBusinesses: React.FC = () => {
  const [businesses, setBusinesses] = useState<{
    traders: BusinessWithType[];
    crafties: BusinessWithType[];
    hospitality: BusinessWithType[];
  }>({
    traders: [],
    crafties: [],
    hospitality: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRecentBusinesses();
  }, []);

  const fetchRecentBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Note: No longer need business type mapping since we fetch from separate tables

      // Fetch recent businesses from all three tables
      const [tradersResult, craftiesResult, hospitalityResult] = await Promise.all([
        // Fetch from traders table (tradesman businesses)
        supabase
          .from('traders')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(3),
        
        // Fetch from crafties table (craftsman businesses)
        supabase
          .from('crafties')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(3),
        
        // Fetch from hospitality table (hospitality businesses)
        supabase
          .from('hospitality')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(3)
      ]);

      if (tradersResult.error) throw tradersResult.error;
      if (craftiesResult.error) throw craftiesResult.error;
      if (hospitalityResult.error) throw hospitalityResult.error;

      // Process businesses from each table separately
      const categorizedBusinesses: {
        traders: BusinessWithType[];
        crafties: BusinessWithType[];
        hospitality: BusinessWithType[];
      } = {
        traders: [],
        crafties: [],
        hospitality: []
      };

      // Process traders (tradesman businesses)
      if (tradersResult.data) {
        for (const business of tradersResult.data) {
          let professionName = '';
          if (business.profession_id) {
            try {
              const { data: profession } = await supabase
                .from('professions')
                .select('name')
                .eq('id', business.profession_id)
                .single();
              professionName = profession?.name || '';
            } catch (error) {
              console.warn('Error fetching tradesman profession:', error);
            }
          }

          categorizedBusinesses.traders.push({
            ...business,
            business_type: 'tradesman',
            profession_name: professionName,
            slug: business.slug
          });
        }
      }

      // Process crafties (craftsman businesses)
      if (craftiesResult.data) {
        for (const business of craftiesResult.data) {
          let professionName = '';
          if (business.profession_id) {
            try {
              const { data: profession } = await supabase
                .from('craft_professions')
                .select('name')
                .eq('id', business.profession_id)
                .single();
              professionName = profession?.name || '';
            } catch (error) {
              console.warn('Error fetching craftsman profession:', error);
            }
          }

          categorizedBusinesses.crafties.push({
            ...business,
            business_type: 'craftsman',
            profession_name: professionName,
            slug: business.slug
          });
        }
      }

      // Process hospitality businesses
      if (hospitalityResult.data) {
        for (const business of hospitalityResult.data) {
          let professionName = '';
          if (business.profession_id) {
            try {
              const { data: profession } = await supabase
                .from('hospitality_categories')
                .select('name')
                .eq('id', business.profession_id)
                .single();
              professionName = profession?.name || '';
            } catch (error) {
              console.warn('Error fetching hospitality category:', error);
            }
          }

          categorizedBusinesses.hospitality.push({
            ...business,
            business_type: 'hospitality',
            profession_name: professionName
          });
        }
      }

      // Stop if we have enough for all categories
      if (categorizedBusinesses.traders.length >= 3 && 
          categorizedBusinesses.crafties.length >= 3 && 
          categorizedBusinesses.hospitality.length >= 3) {
        // Do nothing
      }

      setBusinesses(categorizedBusinesses);
    } catch (err) {
      console.error('Error fetching recent businesses:', err);
      setError('Failed to load recent businesses');
    } finally {
      setLoading(false);
    }
  };

  const getBusinessTypeLabel = (type: string) => {
    switch (type) {
      case 'tradesman':
        return 'Traders';
      case 'craftsman':
        return 'Crafties';
      case 'hospitality':
        return 'Hospitality';
      default:
        return type;
    }
  };

  const getBusinessTypeForCard = (type: string): 'trader' | 'craftsman' | 'hospitality' => {
    switch (type) {
      case 'tradesman':
        return 'trader';
      case 'craftsman':
        return 'craftsman';
      case 'hospitality':
        return 'hospitality';
      default:
        return 'trader';
    }
  };

  if (loading) {
    return (
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Recently Added Businesses</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover the latest professionals and services that have joined our community
            </p>
          </div>
          <div className="flex justify-center items-center h-40">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">Recently Added Businesses</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Recently Added Businesses</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover the latest professionals and services that have joined our community
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Traders Column */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              {getBusinessTypeLabel('tradesman')}
            </h3>
            <div className="space-y-4">
              {businesses.traders.length > 0 ? (
                businesses.traders.map((business) => (
                  <BusinessCard
                    key={`trader-${business.id}`}
                    id={business.id}
                    slug={business.slug}
                    storeName={business.store_name}
                    ownerName={business.owner_name}
                    location={business.location}
                    description={business.description || business.profession_name}
                    avatarUrl={business.avatar_url}
                    businessType={getBusinessTypeForCard(business.business_type)}
                    verified={business.verified}
                  />
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>No recent traders found</p>
                </div>
              )}
            </div>
          </div>

          {/* Crafties Column */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              {getBusinessTypeLabel('craftsman')}
            </h3>
            <div className="space-y-4">
              {businesses.crafties.length > 0 ? (
                businesses.crafties.map((business) => (
                  <BusinessCard
                    key={`craftsman-${business.id}`}
                    id={business.id}
                    slug={business.slug}
                    storeName={business.store_name}
                    ownerName={business.owner_name}
                    location={business.location}
                    description={business.description || business.profession_name}
                    avatarUrl={business.avatar_url}
                    businessType={getBusinessTypeForCard(business.business_type)}
                    verified={business.verified}
                  />
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>No recent crafties found</p>
                </div>
              )}
            </div>
          </div>

          {/* Hospitality Column */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              {getBusinessTypeLabel('hospitality')}
            </h3>
            <div className="space-y-4">
              {businesses.hospitality.length > 0 ? (
                businesses.hospitality.map((business) => (
                  <BusinessCard
                    key={`hospitality-${business.id}`}
                    id={business.id}
                    slug={business.slug}
                    storeName={business.store_name}
                    ownerName={business.owner_name}
                    location={business.location}
                    description={business.description || business.profession_name}
                    avatarUrl={business.avatar_url}
                    businessType={getBusinessTypeForCard(business.business_type)}
                    verified={business.verified}
                  />
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>No recent hospitality businesses found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentlyAddedBusinesses;
